/**
 * Main-thread side of the analysis worker.
 *
 * Two jobs beyond passing messages, both of which exist because a tensor list is
 * something people click through quickly:
 *
 *   **Cancellation.** Selecting tensor B while A is still decoding must abandon
 *   A. Without this, results race and the slower one wins — you click B, briefly
 *   see B, then A's numbers appear over them. A monotonic request id compared on
 *   the way back is enough; the worker is never terminated, because a fresh
 *   worker costs more than letting one stale computation finish and be dropped.
 *
 *   **Caching.** Flipping back to a tensor you just looked at should be free.
 *   Keyed on `(sha, tensor)` — the sha, not the repo, because a deep link pinned
 *   to a commit must never show a different revision's numbers from cache.
 *
 * One worker, not a pool: analysis is per-tensor and user-driven, so a pool
 * optimizes a problem this app does not have while making cancellation harder.
 * The comparison job shares that one worker and, deliberately, the one id
 * sequence: a comparison and a single-tensor analysis compete for the same
 * screen, so starting either must abandon the other.
 */

import type {
  AnalysisResult,
  AnalyzeProgress,
  AnalyzeRequest,
  AnalyzeResponse,
  CompareRequest,
  CompareResponse,
  CompareResult,
  CompareSide,
  WorkerRequest,
} from "./protocol.js";

/**
 * Results are small (a few hundred numbers), so this is bounded by taste rather
 * than memory. Deep enough to make browsing a layer's tensors feel instant.
 */
const CACHE_LIMIT = 48;

/**
 * NUL as the key separator: it cannot occur in a sha, a tensor name or a
 * boolean, so no combination of fields can collide. Written as an escape, never
 * as a literal byte — a raw NUL in source makes grep treat the file as binary
 * and report no matches at all (see scripts/check-no-nul.ts).
 */
const SEPARATOR = "\u0000";

/**
 * Keyed on the sha, not the repo — a deep link pinned to a commit must never
 * show another revision's numbers out of cache — and on the clip-grid flag,
 * since the same tensor analysed with and without the sweep yields different
 * reports.
 */
function cacheKey(input: {
  sha: string;
  tensorName: string;
  includeClipGrid: boolean;
  includeWhatIf: boolean;
}): string {
  return [
    input.sha,
    input.tensorName,
    String(input.includeClipGrid),
    String(input.includeWhatIf),
  ].join(SEPARATOR);
}

/**
 * A comparison is identified by the **pair**, so both shas are in the key.
 *
 * Keying on one side would serve a stale answer the moment the other changed:
 * pinning the base and stepping through three finetunes would show the first
 * finetune's numbers three times, with nothing on screen to suggest anything was
 * wrong. The order matters too and is preserved — A is `relF`'s denominator, so
 * comparing base-to-finetune is not the same computation as the reverse.
 */
function compareCacheKey(input: {
  shaA: string;
  shaB: string;
  tensorName: string;
}): string {
  return [input.shaA, input.shaB, input.tensorName].join(SEPARATOR);
}

export interface AnalyzeInput {
  readonly sha: string;
  readonly tensorName: string;
  readonly dtype: string;
  readonly rows: number;
  readonly cols: number;
  readonly complete: boolean;
  readonly includeClipGrid: boolean;
  readonly includeWhatIf: boolean;
  /** Set when this tensor is a quantized triple; see `AnalyzeRequest.affine`. */
  readonly affine: AnalyzeRequest["affine"];
  /** Set when this tensor is an AWQ/GPTQ group; see `AnalyzeRequest.packed`. */
  readonly packed: AnalyzeRequest["packed"];
  readonly bytes: ArrayBuffer;
  /**
   * Called as the what-if sweep progresses. Not part of the cache key: a cached
   * result is instant, so there is nothing to report.
   */
  readonly onProgress?: ((progress: AnalyzeProgress) => void) | undefined;
}

export interface CompareInput {
  /** Commit sha of A, the baseline. Part of the cache key; see `compareCacheKey`. */
  readonly shaA: string;
  readonly shaB: string;
  /** The tensor name, which both sides share — that is what makes them a pair. */
  readonly tensorName: string;
  readonly a: CompareSide;
  readonly b: CompareSide;
  readonly complete: boolean;
}

/**
 * A promise waiting on the worker, with its type erased.
 *
 * The erasure is confined to this one shape and is what lets both jobs share one
 * pending table and one `onmessage`. It is sound in practice because the id is
 * minted by the same call that fixes the request's kind, so a response can only
 * carry the result type its request asked for — but the compiler cannot see
 * that, and pretending otherwise with a per-job table would duplicate the
 * cancellation logic for no gain.
 */
interface PendingRequest {
  readonly resolve: (result: unknown) => void;
  readonly reject: (error: Error) => void;
  /**
   * Called on every progress tick, if the caller asked for them.
   *
   * Per request rather than per client: two overlapping analyses would
   * otherwise report into each other's UI, and the one whose results are
   * discarded as superseded would still be driving a progress line.
   */
  readonly onProgress?: ((progress: AnalyzeProgress) => void) | undefined;
}

/** Re-insert on hit so recently used entries survive eviction. */
function readCached<T>(cache: Map<string, T>, key: string): T | undefined {
  const cached = cache.get(key);
  if (cached === undefined) return undefined;
  cache.delete(key);
  cache.set(key, cached);
  return cached;
}

function writeCached<T>(cache: Map<string, T>, key: string, value: T): void {
  cache.set(key, value);
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next();
    if (!oldest.done) cache.delete(oldest.value);
  }
}

export class AnalysisClient {
  private worker: Worker | undefined;
  private nextId = 1;
  private latestId = 0;
  private readonly pending = new Map<number, PendingRequest>();
  /** Insertion-ordered, which is what makes the eviction above a real LRU-ish. */
  private readonly cache = new Map<string, AnalysisResult>();
  /** Separate from `cache` only because the value types differ; same discipline. */
  private readonly compareCache = new Map<string, CompareResult>();

  /**
   * Created lazily so merely loading the page doesn't spin up a worker — most
   * visitors browse the tensor list before clicking anything.
   */
  private ensureWorker(): Worker {
    if (this.worker) return this.worker;
    // `new URL(..., import.meta.url)` is the form Vite statically analyses to
    // bundle the worker; a string path would break in the production build.
    this.worker = new Worker(new URL("./analyze.worker.ts", import.meta.url), {
      type: "module",
    });
    // Without this, a worker that fails to evaluate its module graph — a bad
    // import, a syntax error, a bundler misconfiguration — never posts anything
    // back, and every pending promise hangs forever with the UI stuck on
    // "Reading bytes…". Silent and indistinguishable from a slow network.
    this.worker.onerror = (event) => {
      const message =
        event instanceof ErrorEvent && event.message
          ? event.message
          : "the analysis worker failed to start";
      this.failAllPending(new Error(`Analysis worker error: ${message}`));
    };
    this.worker.onmessageerror = () => {
      this.failAllPending(
        new Error("Analysis worker sent a message that could not be read"),
      );
    };
    this.worker.onmessage = (
      event: MessageEvent<AnalyzeResponse | CompareResponse>,
    ) => {
      const message = event.data;
      const entry = this.pending.get(message.id);
      if (!entry) return;
      /*
       * Progress first, and without touching `pending`.
       *
       * A tick means "still working". Falling through to the settle below would
       * resolve the promise with `undefined` on the first one, and every sweep
       * would appear to succeed instantly with no results — which is why the
       * message is discriminated on its own field rather than on the absence of
       * `ok`.
       */
      if ("progress" in message) {
        entry.onProgress?.(message);
        return;
      }
      this.pending.delete(message.id);
      if (message.ok) entry.resolve(message.result);
      else entry.reject(new Error(message.error));
    };
    return this.worker;
  }

  /** True when this result is already known, so the caller can skip fetching. */
  peek(
    sha: string,
    tensorName: string,
    includeClipGrid: boolean,
    includeWhatIf: boolean,
  ): AnalysisResult | undefined {
    return this.cache.get(
      cacheKey({ sha, tensorName, includeClipGrid, includeWhatIf }),
    );
  }

  /**
   * Analyze, or return a cached result.
   *
   * Rejects with an `AbortError` when a newer request supersedes this one — the
   * caller should treat that as "not my problem any more", not as a failure to
   * show the visitor.
   */
  async analyze(input: AnalyzeInput): Promise<AnalysisResult> {
    const key = cacheKey(input);
    const cached = readCached(this.cache, key);
    if (cached) return cached;

    const id = this.claimId();
    const request: AnalyzeRequest = {
      kind: "analyze",
      id,
      dtype: input.dtype,
      rows: input.rows,
      cols: input.cols,
      complete: input.complete,
      includeClipGrid: input.includeClipGrid,
      includeWhatIf: input.includeWhatIf,
      affine: input.affine,
      packed: input.packed,
      bytes: input.bytes,
    };

    // Transfer, don't structured-clone: for a 64 MB tensor the copy alone would
    // cost more than the analysis. `input.bytes` is unusable here afterwards,
    // which is fine — nothing else holds a reference. The plane buffers go the
    // same way; they are small, but transferring is strictly cheaper than
    // cloning and keeps one rule rather than two.
    const result = await this.send<AnalysisResult>(
      request,
      transferable([request]),
      input.onProgress,
    );

    this.assertCurrent(id);
    writeCached(this.cache, key, result);
    return result;
  }

  /**
   * Compare A against B, or return a cached result.
   *
   * Same cancellation and the same cache discipline as `analyze`, and the same
   * `AbortError` on being superseded. The two jobs share the id sequence on
   * purpose — see the module note.
   */
  async compare(input: CompareInput): Promise<CompareResult> {
    const key = compareCacheKey(input);
    const cached = readCached(this.compareCache, key);
    if (cached) return cached;

    const id = this.claimId();
    const request: CompareRequest = {
      kind: "compare",
      id,
      a: input.a,
      b: input.b,
      complete: input.complete,
    };

    // Both sides' payloads and both sides' planes, deduplicated: comparing a
    // tensor against itself hands the same `ArrayBuffer` in twice, and naming a
    // buffer twice in one transfer list is a `DataCloneError` rather than a
    // no-op. Structured cloning preserves identity within a message, so the
    // worker still sees both sides pointing at the one buffer.
    const result = await this.send<CompareResult>(
      request,
      transferable([input.a, input.b]),
    );

    this.assertCurrent(id);
    writeCached(this.compareCache, key, result);
    return result;
  }

  /** The next id, recorded as the newest — everything older is now stale. */
  private claimId(): number {
    const id = this.nextId++;
    this.latestId = id;
    return id;
  }

  /**
   * Reject an already-resolved request whose answer nobody wants any more.
   *
   * Checked *after* the round trip rather than before: the worker is not
   * terminated, so a superseded computation still finishes and still posts back,
   * and this is where its result is dropped instead of rendered.
   */
  private assertCurrent(id: number): void {
    if (id !== this.latestId) {
      throw new DOMException("Superseded by a newer selection", "AbortError");
    }
  }

  /** Post one request and await its reply. The only place ids are registered. */
  private send<T>(
    request: WorkerRequest,
    transfers: ArrayBuffer[],
    onProgress?: (progress: AnalyzeProgress) => void,
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.pending.set(request.id, {
        resolve: resolve as (result: unknown) => void,
        reject,
        onProgress,
      });
      this.ensureWorker().postMessage(request, transfers);
    });
  }

  /**
   * Reject every outstanding request. Used only for worker-level failures,
   * which are not attributable to any one request — the worker is gone, so
   * nothing pending can ever be answered.
   */
  private failAllPending(error: Error): void {
    for (const [, entry] of this.pending) entry.reject(error);
    this.pending.clear();
    // Drop the handle so the next attempt constructs a fresh worker rather than
    // queueing onto a dead one.
    this.worker = undefined;
  }

  /** Mark everything in flight as stale without tearing down the worker. */
  cancelInFlight(): void {
    this.latestId = this.nextId;
  }

  /** True while a request newer than `id` has not superseded it. */
  isCurrent(id: number): boolean {
    return id === this.latestId;
  }
}

/**
 * Every buffer in these payloads, each named once.
 *
 * A `Set` rather than an array because the same `ArrayBuffer` can legitimately
 * appear twice — a tensor compared against itself, or two sides that share a
 * plane — and `postMessage` throws on a duplicate entry in the transfer list.
 */
function transferable(
  payloads: ReadonlyArray<Pick<CompareSide, "bytes" | "affine">>,
): ArrayBuffer[] {
  const buffers = new Set<ArrayBuffer>();
  for (const payload of payloads) {
    buffers.add(payload.bytes);
    if (payload.affine) {
      buffers.add(payload.affine.scales);
      buffers.add(payload.affine.biases);
    }
  }
  return [...buffers];
}
